import { useState } from "react";
import type { WorkplaceReminderDeepLink } from "@/lib/messages/workplaceReminderModel";

export type ReassignMemberOption = {
  user_id: string;
  label: string;
};

type Props = {
  notificationId: string;
  deepLink: WorkplaceReminderDeepLink;
  canReassign: boolean;
  reassignMembers?: ReassignMemberOption[];
  onDismiss?: (notificationId: string) => void;
  onResolve?: (notificationId: string, reason?: string) => void;
  onReassign?: (notificationId: string, assigneeUserId: string, reason?: string) => void;
  onNavigate?: (link: WorkplaceReminderDeepLink) => void;
};

export function WorkplaceReminderHygieneActions({
  notificationId,
  deepLink,
  canReassign,
  reassignMembers = [],
  onDismiss,
  onResolve,
  onReassign,
  onNavigate,
}: Props) {
  const [panel, setPanel] = useState<"none" | "resolve" | "reassign">("none");
  const [reason, setReason] = useState("");
  const [assignee, setAssignee] = useState("");

  const canNavigate = Boolean(onNavigate && (deepLink.lead_id || deepLink.notification_id));

  return (
    <div className="shrink-0 space-y-1.5">
      <div className="flex flex-wrap justify-end gap-2">
        {canNavigate ? (
          <button
            type="button"
            className="text-[10px] font-medium underline opacity-80 hover:opacity-100"
            onClick={() => onNavigate!(deepLink)}
          >
            Open lead
          </button>
        ) : null}
        {onResolve ? (
          <button
            type="button"
            className="text-[10px] font-medium underline opacity-80 hover:opacity-100"
            onClick={() => setPanel(panel === "resolve" ? "none" : "resolve")}
          >
            Resolve
          </button>
        ) : null}
        {canReassign && onReassign && reassignMembers.length > 0 ? (
          <button
            type="button"
            className="text-[10px] font-medium underline opacity-80 hover:opacity-100"
            onClick={() => setPanel(panel === "reassign" ? "none" : "reassign")}
          >
            Reassign
          </button>
        ) : null}
        {onDismiss ? (
          <button
            type="button"
            className="text-[10px] font-medium underline opacity-80 hover:opacity-100"
            onClick={() => onDismiss(notificationId)}
          >
            Dismiss
          </button>
        ) : null}
      </div>

      {panel === "resolve" && onResolve ? (
        <div className="rounded border border-indigo-200/70 bg-white/60 px-2 py-1.5 dark:border-indigo-800 dark:bg-zinc-950/40">
          <label className="block text-[10px] opacity-90">
            Resolution note (optional, visible on lead timeline)
            <textarea
              className="mt-0.5 w-full min-h-[2.5rem] rounded border border-gray-200 px-1.5 py-1 text-[10px] dark:border-zinc-600 dark:bg-zinc-900"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
            />
          </label>
          <button
            type="button"
            className="mt-1 text-[10px] font-semibold underline"
            onClick={() => {
              onResolve(notificationId, reason.trim() || undefined);
              setPanel("none");
              setReason("");
            }}
          >
            Confirm resolve
          </button>
        </div>
      ) : null}

      {panel === "reassign" && onReassign ? (
        <div className="rounded border border-indigo-200/70 bg-white/60 px-2 py-1.5 dark:border-indigo-800 dark:bg-zinc-950/40">
          <label className="block text-[10px] opacity-90">
            Reassign to
            <select
              className="mt-0.5 w-full rounded border border-gray-200 px-1.5 py-1 text-[10px] dark:border-zinc-600 dark:bg-zinc-900"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
            >
              <option value="">Select teammate…</option>
              {reassignMembers.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label className="mt-1 block text-[10px] opacity-90">
            Note (optional)
            <input
              className="mt-0.5 w-full rounded border border-gray-200 px-1.5 py-1 text-[10px] dark:border-zinc-600 dark:bg-zinc-900"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
            />
          </label>
          <button
            type="button"
            disabled={!assignee}
            className="mt-1 text-[10px] font-semibold underline disabled:opacity-40"
            onClick={() => {
              if (!assignee) return;
              onReassign(notificationId, assignee, reason.trim() || undefined);
              setPanel("none");
              setReason("");
              setAssignee("");
            }}
          >
            Confirm reassign
          </button>
        </div>
      ) : null}
    </div>
  );
}
